// components/SignalsList.tsx
import { getServiceColor } from 'constants/serviceColors';
import FeatherIcon from 'feather-icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Collapse, Pagination } from 'react-bootstrap';
import { videoProjectService } from 'services/videoService';

interface SignalsListProps {
  savedSigns: any[];
  images: any[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onSelect?: (index: number | null) => void;
  selectedIndex?: number | null;
  isBlinking?: boolean;
  formatTime: (time: number) => string;
}

const SignalsList: React.FC<SignalsListProps> = ({
  savedSigns,
  images,
  onEdit,
  onDelete,
  onSelect,
  selectedIndex,
  isBlinking,
  formatTime
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [regions, setRegions] = useState<any[]>([]);
  const [regionsLoaded, setRegionsLoaded] = useState(false);

  // Carregar regiões uma única vez
  useEffect(() => {
    const loadRegions = async () => {
      if (regionsLoaded) return;

      try {
        const response = await videoProjectService.getSignalRegions();
        setRegions(response);
        setRegionsLoaded(true);
      } catch (error) {
        console.error('Erro ao carregar regiões:', error);
      }
    };

    if (savedSigns.length > 0) {
      loadRegions();
    }
  }, [savedSigns.length, regionsLoaded]);

  // Função para buscar nome da região pelo ID
  const getRegionName = (regionId: number | null) => {
    if (!regionId) return null;
    const region = regions.find(r => r.id === regionId);
    return region?.name || null;
  };

  const formatTypeName = (type: string) => {
    const typeNames: { [key: string]: string } = {
      regulamentacao: 'Regulamentação',
      advertencia: 'Advertência',
      auxiliares: 'Auxiliares',
      indicativa: 'Indicativa'
    };
    return typeNames[type] || type;
  };

  const getSignImagePath = (device: any) => {
    if (!device) return '';
    return `/images/signs/${device.symbol_name}`;
  };

  const filteredSigns = useMemo(() => {
    if (!searchTerm) return savedSigns;

    const term = searchTerm.toLowerCase();

    return savedSigns.filter(sign => {
      switch (filterType) {
        case 'service': {
          const serviceName =
            sign.service_id === 1
              ? 'implantação'
              : sign.service_id === 2
              ? 'remoção'
              : sign.service_id === 3
              ? 'limpeza'
              : sign.service_id === 4
              ? 'manter'
              : '';
          return serviceName.includes(term);
        }
        case 'code':
          return (sign.signal_device?.code || '').toLowerCase().includes(term);

        case 'type': {
          const typeName = formatTypeName(sign.signal_device?.type || '');
          return typeName.toLowerCase().includes(term);
        }
        case 'all':
        default: {
          const service =
            sign.service_id === 1
              ? 'implantação'
              : sign.service_id === 2
              ? 'remoção'
              : sign.service_id === 3
              ? 'limpeza'
              : sign.service_id === 4
              ? 'manter'
              : '';
          const code = (sign.signal_device?.code || '').toLowerCase();
          const name = (sign.signal_device?.name || '').toLowerCase();
          const type = formatTypeName(
            sign.signal_device?.type || ''
          ).toLowerCase();
          const support = (sign.signal_support?.name || '').toLowerCase();

          return (
            service.includes(term) ||
            code.includes(term) ||
            name.includes(term) ||
            type.includes(term) ||
            support.includes(term)
          );
        }
      }
    });
  }, [savedSigns, searchTerm, filterType]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = useMemo(() => {
    return filteredSigns.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredSigns, indexOfFirstItem, indexOfLastItem]);

  const totalPages = Math.ceil(filteredSigns.length / itemsPerPage);

  useEffect(() => {
    if (selectedIndex !== null && selectedIndex !== undefined) {
      const itemInFilteredIndex = filteredSigns.findIndex(sign => {
        const originalIndex = savedSigns.indexOf(sign);
        return originalIndex === selectedIndex;
      });

      if (itemInFilteredIndex !== -1) {
        const targetPage = Math.floor(itemInFilteredIndex / itemsPerPage) + 1;

        if (targetPage !== currentPage) {
          console.log(
            `📄 Navegando para página ${targetPage} para mostrar item #${
              selectedIndex + 1
            }`
          );
          setCurrentPage(targetPage);
          setExpandedItems(new Set([selectedIndex]));
        }
      }
    }
  }, [selectedIndex, itemsPerPage, filteredSigns, savedSigns, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const toggleItemExpansion = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandedItems(new Set());
    } else {
      const allIndices = currentItems.map((_, idx) => indexOfFirstItem + idx);
      setExpandedItems(new Set(allIndices));
    }
    setExpandAll(!expandAll);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    setExpandedItems(new Set());
    setExpandAll(false);
  };

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setExpandedItems(new Set());
    setExpandAll(false);
  };

  const getPaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;

    const selectedPage =
      selectedIndex !== null && selectedIndex !== undefined
        ? Math.floor(selectedIndex / itemsPerPage) + 1
        : null;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
            style={
              i === selectedPage && i !== currentPage
                ? {
                    backgroundColor: '#fff3cd',
                    borderColor: '#ffc107',
                    color: '#856404'
                  }
                : {}
            }
          >
            {i}
          </Pagination.Item>
        );
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage < maxPagesToShow - 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      if (startPage > 1) {
        items.push(
          <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
            1
          </Pagination.Item>
        );
        if (startPage > 2) {
          items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
            style={
              i === selectedPage && i !== currentPage
                ? {
                    backgroundColor: '#fff3cd',
                    borderColor: '#ffc107',
                    color: '#856404'
                  }
                : {}
            }
          >
            {i}
          </Pagination.Item>
        );
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }
        items.push(
          <Pagination.Item
            key={totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </Pagination.Item>
        );
      }
    }

    return items;
  };

  if (!savedSigns || savedSigns.length === 0) {
    return (
      <Card>
        <Card.Body>
          <div className="text-center text-muted py-5">
            <FeatherIcon icon="inbox" size={48} className="mb-3" />
            <p className="mb-0">Nenhuma sinalização cadastrada ainda.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <h6 className="mb-0">Sinalizações Cadastradas</h6>
          <Button
            variant="link"
            size="sm"
            onClick={toggleExpandAll}
            className="text-decoration-none p-0"
          >
            <FeatherIcon
              icon={expandAll ? 'minimize-2' : 'maximize-2'}
              size={16}
              className="me-1"
            />
            <small>{expandAll ? 'Recolher' : 'Expandir'} Todos</small>
          </Button>
        </div>

        <div className="d-flex align-items-center gap-2">
          <div
            className="input-group input-group-sm"
            style={{ width: '300px' }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <select
              className="form-select"
              style={{ maxWidth: '120px' }}
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
            >
              <option value="all">Tudo</option>
              <option value="service">Serviço</option>
              <option value="code">Código</option>
              <option value="type">Tipo</option>
            </select>
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm('')}
              >
                <FeatherIcon icon="x" size={14} />
              </button>
            )}
          </div>

          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value={5}>5 por página</option>
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
          </select>
        </div>
      </Card.Header>

      <Card.Body>
        {currentItems.map(sign => {
          const originalIndex = savedSigns.indexOf(sign);
          const isExpanded = expandedItems.has(originalIndex);
          const isSelected = originalIndex === selectedIndex;

          const correspondingImage = images.find(
            img => img.id === sign.imageId
          );

          return (
            <div
              key={originalIndex}
              onClick={() => onSelect?.(originalIndex)}
              className={`p-3 mb-2 border rounded ${
                isSelected
                  ? isBlinking
                    ? 'border-warning bg-warning bg-opacity-10 blink-item'
                    : 'border-primary bg-primary bg-opacity-10'
                  : 'border-light'
              }`}
              style={{
                cursor: onSelect ? 'pointer' : 'default',
                transition: 'all 0.2s',
                animation:
                  isSelected && isBlinking ? 'blink 1s ease-in-out 3' : 'none'
              }}
            >
              <div className="d-flex align-items-center gap-3">
                {/* ✅ CÍRCULO COM NÚMERO NO CANTO ESQUERDO */}
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    minWidth: '36px',
                    borderRadius: '50%',
                    backgroundColor: getServiceColor(sign.service_id).rgbaColor,
                    border: isSelected
                      ? '2px solid #0d6efd'
                      : '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isSelected
                      ? '0 0 0 3px rgba(13, 110, 253, 0.3), 0 2px 4px rgba(0,0,0,0.3)'
                      : '0 2px 4px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <span
                    style={{
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      fontFamily: 'Arial, sans-serif',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}
                  >
                    {originalIndex + 1}
                  </span>
                </div>

                <div style={{ flex: 1 }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span
                      className={`badge ${
                        sign.service_id === 1
                          ? 'bg-success'
                          : sign.service_id === 2
                          ? 'bg-danger'
                          : sign.service_id === 3
                          ? 'bg-info'
                          : sign.service_id === 4
                          ? 'bg-secondary'
                          : 'bg-light'
                      }`}
                    >
                      {sign.service_id === 1
                        ? 'Implantação'
                        : sign.service_id === 2
                        ? 'Remoção'
                        : sign.service_id === 3
                        ? 'Limpeza'
                        : sign.service_id === 4
                        ? 'Manter'
                        : 'N/A'}
                    </span>

                    {sign.signal_film_id && (
                      <span className="badge bg-secondary">
                        {sign.signal_film_id === 1 ? 'Tipo I' : 'Tipo III'}
                      </span>
                    )}

                    {isSelected && (
                      <span className="badge bg-warning text-dark">
                        <FeatherIcon
                          icon="crosshair"
                          size={12}
                          className="me-1"
                        />
                        Selecionado
                      </span>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    {sign.signal_device?.symbol_name && (
                      <img
                        src={getSignImagePath(sign.signal_device)}
                        alt={sign.signal_device.code}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'contain'
                        }}
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}

                    <div style={{ flex: 1 }}>
                      <strong>{sign.signal_device?.code || 'N/A'}</strong>
                      <br />
                      <small className="text-muted">
                        {sign.signal_device?.name || 'Sem nome'}
                      </small>

                      {sign.signal_regions_id && (
                        <>
                          <br />
                          <small className="text-muted">
                            <FeatherIcon
                              icon="maximize"
                              size={12}
                              className="me-1"
                            />
                            {getRegionName(sign.signal_regions_id) ||
                              `ID ${sign.signal_regions_id}`}
                            {sign.signal_region_area && (
                              <span className="ms-1">
                                ({sign.signal_region_area})
                              </span>
                            )}
                          </small>
                        </>
                      )}
                    </div>

                    {!isExpanded && sign.signal_support && (
                      <div>
                        <small className="text-muted">
                          <strong>Suporte:</strong> {sign.signal_support.code}
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-1 ms-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      toggleItemExpansion(originalIndex);
                    }}
                    title={isExpanded ? 'Recolher' : 'Expandir'}
                  >
                    <FeatherIcon
                      icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                      size={16}
                    />
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onEdit(originalIndex);
                    }}
                    title="Editar"
                  >
                    <FeatherIcon icon="edit-2" size={16} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onDelete(originalIndex);
                    }}
                    title="Excluir"
                  >
                    <FeatherIcon icon="trash-2" size={16} />
                  </Button>
                </div>
              </div>

              <Collapse in={isExpanded}>
                <div className="mt-2 pt-2 border-top">
                  <div className="row g-2">
                    {/* ID do Marker */}
                    {sign.id && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">
                          <strong>ID:</strong> {sign.id}
                        </small>
                      </div>
                    )}

                    {sign.signal_device?.description && (
                      <div className="col-12">
                        <small className="text-muted d-block">
                          <strong>Descrição:</strong>{' '}
                          {sign.signal_device.description}
                        </small>
                      </div>
                    )}

                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        <strong>Tipo:</strong>{' '}
                        {formatTypeName(sign.signal_device?.type || 'N/A')}
                      </small>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        <strong>Coordenadas X,Y:</strong> {sign.x.toFixed(3)},{' '}
                        {sign.y.toFixed(3)}
                      </small>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        <strong>Frame:</strong> {sign.frameNumber}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        <strong>Tempo:</strong> {formatTime(sign.timestamp)}
                      </small>
                    </div>

                    {correspondingImage &&
                      correspondingImage.lat !== null &&
                      correspondingImage.long !== null && (
                        <div className="col-12">
                          <small className="text-muted d-block">
                            <strong>GPS Original:</strong>{' '}
                            {correspondingImage.lat.toFixed(7)},{' '}
                            {correspondingImage.long.toFixed(7)}
                          </small>
                        </div>
                      )}

                    {sign.adjusted_lat && sign.adjusted_lng && (
                      <div className="col-12">
                        <div className="p-2 bg-info bg-opacity-10 rounded">
                          <small className="text-info d-block">
                            <FeatherIcon
                              icon="crosshair"
                              size={12}
                              className="me-1"
                            />
                            <strong>GPS Ajustado:</strong>{' '}
                            {sign.adjusted_lat.toFixed(7)},{' '}
                            {sign.adjusted_lng.toFixed(7)}
                          </small>
                          {(sign.horizontal_offset || sign.depth_offset) && (
                            <small className="text-muted d-block mt-1">
                              Offset H:{' '}
                              {sign.horizontal_offset?.toFixed(2) || '0.00'}m |
                              D: {sign.depth_offset?.toFixed(2) || '0.00'}m
                            </small>
                          )}
                        </div>
                      </div>
                    )}

                    {sign.service_id && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">
                          <strong>Serviço:</strong>{' '}
                          {sign.service_id === 1
                            ? 'Implantação'
                            : sign.service_id === 2
                            ? 'Remoção'
                            : sign.service_id === 3
                            ? 'Limpeza'
                            : sign.service_id === 4
                            ? 'Manter'
                            : 'N/A'}
                        </small>
                      </div>
                    )}

                    {sign.signal_film_id && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">
                          <strong>Película:</strong>{' '}
                          {sign.signal_film_id === 1 ? 'Tipo I' : 'Tipo III'}
                        </small>
                      </div>
                    )}

                    {/* Região (Tamanho da Placa) */}
                    {sign.signal_regions_id && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">
                          <strong>Tamanho da Placa:</strong>{' '}
                          {getRegionName(sign.signal_regions_id) ||
                            `ID ${sign.signal_regions_id}`}
                        </small>
                      </div>
                    )}

                    {/* Área da Placa */}
                    {sign.signal_region_area && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">
                          <strong>Área da Placa:</strong>{' '}
                          {sign.signal_region_area}
                        </small>
                      </div>
                    )}

                    {/* Suporte */}
                    {sign.signal_support && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">
                          <strong>Suporte:</strong> {sign.signal_support.code} -{' '}
                          {sign.signal_support.name}
                        </small>
                      </div>
                    )}

                    {/* Observações */}
                    {sign.comments && (
                      <div className="col-12">
                        <small className="text-muted d-block">
                          <strong>Observações:</strong> {sign.comments}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })}

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination size="sm">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {getPaginationItems()}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card.Body>
      <Card.Footer className="text-muted">
        <small>
          <strong>Total:</strong> {filteredSigns.length}
          {searchTerm && ` (de ${savedSigns.length} total)`} |
          <strong> Com suporte:</strong>{' '}
          {filteredSigns.filter(s => s.signal_support).length} |
          <strong> Sem suporte:</strong>{' '}
          {filteredSigns.filter(s => !s.signal_support).length} |
          <strong> Com GPS ajustado:</strong>{' '}
          {filteredSigns.filter(s => s.adjusted_lat && s.adjusted_lng).length}
        </small>
      </Card.Footer>
    </Card>
  );
};

export default SignalsList;
